<script setup lang="ts">
import { computed, nextTick, onMounted, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import type { SearchHit, SearchTermStat } from '@s-blog/shared-types';
import { searchApi, type AskSource } from '@/api/public';
import { apiErrorMessage } from '@/api/http';
import { useLang } from '@/composables/useLang';
import { useFeatureFlags } from '@/composables/useFeatureFlags';

const props = defineProps<{ open: boolean }>();
const emit = defineEmits<{ (e: 'close'): void }>();

const route = useRoute();
const router = useRouter();
const { isZh } = useLang();
const { features } = useFeatureFlags(); // RAG 关掉时隐藏「问我的博客」入口 (纯搜索仍可用)

const q = ref('');
const hits = ref<SearchHit[]>([]);
const loading = ref(false);
const error = ref<string | null>(null);
const inputEl = ref<HTMLInputElement | null>(null);

// ── RAG「问我的博客」多轮对话态 ──
interface ConvTurn { role: 'user' | 'assistant'; content: string; sources?: AskSource[] }
const asking = ref(false); // 是否在问答模式 (显示对话区而非搜索结果)
const turns = ref<ConvTurn[]>([]); // 整段对话 (user/assistant 交替)
const askLoading = ref(false); // 当前轮等首个 token
const streaming = ref(false); // 全程流式中, 驱动 panel 彩色辉光
let abortAsk: (() => void) | null = null;
const convoEl = ref<HTMLElement | null>(null); // 对话滚动容器, 新内容自动滚到底

// qwen 流式输出的是 markdown（**加粗** / 列表 / 行内代码），实时 render 成净化后的 HTML。
// 正文走的是后端预渲染 contentHtml，但 RAG 答案是浏览器侧实时生成，只能客户端 render。
marked.setOptions({ breaks: true, gfm: true });
function renderMd(s: string): string {
  return s ? (DOMPurify.sanitize(marked.parse(s, { async: false }) as string)) : '';
}
function scrollConvoToBottom() {
  nextTick(() => { if (convoEl.value) convoEl.value.scrollTop = convoEl.value.scrollHeight; });
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

// iOS Safari 友好的滚动锁：单纯 overflow:hidden 不能阻止 touch 滚动。
// 用 position:fixed + top:-scrollY 锁住整页，关闭时恢复滚动位置
let lockedScrollY = 0;
function lockScroll() {
  lockedScrollY = window.scrollY;
  document.body.style.position = 'fixed';
  document.body.style.top = `-${lockedScrollY}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
}
function unlockScroll() {
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.left = '';
  document.body.style.right = '';
  document.body.style.width = '';
  window.scrollTo(0, lockedScrollY);
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      // 从 URL ?q= 初始化（首次访问 /?q=docker 这种 deep link）
      const urlQ = typeof route.query.q === 'string' ? route.query.q : '';
      if (urlQ && !q.value) q.value = urlQ;
      await nextTick();
      inputEl.value?.focus();
      lockScroll();
    } else {
      unlockScroll();
    }
  },
);

// 同步 URL ?q=xxx：让搜索状态可分享 / 书签 / 后退
function syncUrl(next: string) {
  // 仅在 overlay 打开时同步，避免关闭后用户已经导航到别的路由还误改 query
  if (!props.open) return;
  const trimmed = next.trim();
  const current = typeof route.query.q === 'string' ? route.query.q : '';
  if (trimmed === current) return;
  const nextQuery = { ...route.query };
  if (trimmed) nextQuery.q = trimmed;
  else delete nextQuery.q;
  // replace 不入 history，连续敲字不堆叠后退栈
  router.replace({ query: nextQuery });
}

watch(q, (next) => {
  if (debounceTimer) clearTimeout(debounceTimer);
  // 问答态: 输入框用来打追问, 不跑后台关键词搜索 (否则失败会把 error 漏进答案区)
  if (asking.value) return;
  if (!next.trim()) {
    hits.value = [];
    error.value = null;
    loading.value = false;
    syncUrl('');
    return;
  }
  loading.value = true;
  error.value = null;
  debounceTimer = setTimeout(async () => {
    try {
      const res = await searchApi.search(next.trim(), 12);
      // 过期请求保护：用户已经又敲新字了
      if (next !== q.value) return;
      hits.value = res.hits;
      syncUrl(next);
    } catch (e) {
      error.value = apiErrorMessage(e);
      hits.value = [];
    } finally {
      loading.value = false;
    }
  }, 240);
});

// 只清 UI 状态 + 关 overlay, 不动路由 (给"点结果跳转"用: push 已经带走 ?q, 不能再 replace 否则会覆盖跳转)
function closeUi() {
  abortAsk?.();
  q.value = '';
  hits.value = [];
  error.value = null;
  resetAsk();
  emit('close');
}

function close() {
  // 关闭时清掉 URL ?q= 参数 (仅"关闭"按钮/ESC 用; 不涉及跳转)
  if (typeof route.query.q === 'string') {
    const nextQuery = { ...route.query };
    delete nextQuery.q;
    router.replace({ query: nextQuery });
  }
  closeUi();
}

function resetAsk() {
  abortAsk?.();
  abortAsk = null;
  asking.value = false;
  turns.value = [];
  askLoading.value = false;
  streaming.value = false;
}

// 发一轮 (首问 or 追问): 不清历史, 追加 user + 空 assistant, 流式填当前 assistant turn
function sendTurn() {
  if (!features.value.rag) return; // 站长关了 RAG 问答
  const question = q.value.trim();
  if (!question || streaming.value) return;
  abortAsk?.();
  asking.value = true;
  error.value = null;
  // 此前历史 (本轮之前的对话) 发给后端做多轮上下文
  const history = turns.value.map((t) => ({ role: t.role, content: t.content }));
  turns.value.push({ role: 'user', content: question });
  const ai: ConvTurn = { role: 'assistant', content: '', sources: [] };
  turns.value.push(ai);
  q.value = '';
  askLoading.value = true;
  streaming.value = true;
  scrollConvoToBottom();
  abortAsk = searchApi.ask(question, history, {
    onToken: (t) => { ai.content += t; askLoading.value = false; scrollConvoToBottom(); },
    onSources: (s) => { ai.sources = s; scrollConvoToBottom(); },
    onError: (m) => { error.value = m; askLoading.value = false; streaming.value = false; },
    onDone: () => {
      askLoading.value = false;
      streaming.value = false;
      // 答完聚焦输入框 (不全选, 直接打字就是追问)
      nextTick(() => inputEl.value?.focus());
    },
  });
}

// 从对话态返回搜索结果态 (清空对话)
function backToSearch() {
  resetAsk();
  nextTick(() => inputEl.value?.focus());
}

function goSource(s: AskSource) {
  router.push(s.url);
  closeUi(); // 用 closeUi 不用 close: 避免 router.replace 覆盖上面的 push
}

function onKey(e: KeyboardEvent) {
  if (!props.open) return;
  if (e.key === 'Escape') close();
}

function pathOf(hit: SearchHit): string {
  if (hit.type === 'POST') return `/blog/${hit.slug}`;
  if (hit.type === 'WORK') return `/works/${hit.slug}`;
  if (hit.type === 'SKILL') return `/skills/${hit.slug}`;
  if (hit.type === 'MCP') return `/mcps/${hit.slug}`;
  return '/now';
}

function go(hit: SearchHit) {
  router.push(pathOf(hit));
  closeUi(); // 同 goSource: 别用 close (router.replace 会覆盖跳转)
}

// ── 热门搜索词 + 推荐问题（onMounted 各取一次，失败/空静默降级）──
const hotSearchTerms = ref<SearchTermStat[]>([]);
const hotAskTerms = ref<SearchTermStat[]>([]);

// 点击热门搜索词 → 填入搜索框（watch(q) 会自动触发搜索）
function pickSearchTerm(term: string) {
  q.value = term;
}

// 点击推荐问题 → 填入 + 发起提问
function pickAskTerm(term: string) {
  q.value = term;
  nextTick(() => sendTurn());
}

onMounted(() => {
  window.addEventListener('keydown', onKey);
  // 并行拉取，失败静默忽略，空数组时对应区块不渲染
  searchApi.topTerms('search', 8).then((r) => { hotSearchTerms.value = r; }).catch(() => {});
  searchApi.topTerms('ask', 6).then((r) => { hotAskTerms.value = r; }).catch(() => {});
});
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey);
  if (debounceTimer) clearTimeout(debounceTimer);
  // 兜底解锁：组件卸载时如果还是 open 状态，避免页面卡死
  if (props.open) unlockScroll();
});

const placeholder = computed(() =>
  isZh.value ? '搜文章 / 笔记 / 作品 / 此刻' : 'Search posts, notes, works, now…',
);
</script>

<template>
  <!-- Teleport 到 body：脱离 TopNav (sticky+z-index:50) 的 stacking context，
       否则 fixed inset:0 的 overlay 会被 RouterView 内的内容遮挡 hit-test -->
  <Teleport to="body">
    <Transition name="overlay">
      <div
        v-if="open"
        id="search-overlay"
        class="search"
        role="dialog"
        aria-modal="true"
        :aria-label="isZh ? '全站搜索' : 'Site search'"
        @click.self="close"
      >
      <div class="search__panel" :class="{ 'search__panel--glow': asking, 'search__panel--active': streaming }">
        <div class="search__head">
          <span class="search__icon" aria-hidden>{{ asking ? '✨' : '⌕' }}</span>
          <input
            ref="inputEl"
            v-model="q"
            class="search__input"
            type="text"
            :placeholder="asking ? (isZh ? '继续问…' : 'Ask again…') : placeholder"
            autocomplete="off"
            spellcheck="false"
            @keydown.enter="q.trim() && sendTurn()"
          />
          <button class="search__close" @click="close" :aria-label="isZh ? '关闭' : 'close'">
            ESC
          </button>
        </div>

        <!-- 对话态: RAG 多轮问答 (气泡) + 每轮来源 -->
        <div v-if="asking" class="search__body">
          <div ref="convoEl" class="ask__convo">
            <template v-for="(t, i) in turns" :key="i">
              <div v-if="t.role === 'user'" class="ask__bubble ask__bubble--user">{{ t.content }}</div>
              <div v-else class="ask__bubble ask__bubble--ai">
                <div v-if="!t.content && askLoading && i === turns.length - 1" class="search__hint">
                  {{ isZh ? '✨ 正在阅读相关文章…' : '✨ reading articles…' }}
                </div>
                <div v-else class="ask__text aurora-prose-mini" v-html="renderMd(t.content)"></div>
                <div v-if="t.sources && t.sources.length" class="ask__sources">
                  <div class="ask__sources-label">{{ isZh ? '来源' : 'Sources' }}</div>
                  <button
                    v-for="s in t.sources"
                    :key="`${s.type}:${s.slug}`"
                    type="button"
                    class="ask__source"
                    @click="goSource(s)"
                  >
                    <span class="search__hit-type">{{ s.type }}</span>
                    <span class="ask__source-title">{{ s.title }}</span>
                  </button>
                </div>
              </div>
            </template>
            <!-- 推荐问题：首次进入问答态（还没任何对话）时显示 -->
            <div v-if="turns.length === 0 && hotAskTerms.length > 0" class="ask__suggestions">
              <div class="ask__suggestions-label">{{ isZh ? '试试问' : 'Try asking' }}</div>
              <div class="ask__suggestions-chips">
                <button
                  v-for="term in hotAskTerms"
                  :key="term.query"
                  type="button"
                  class="ask__suggestion-chip"
                  @click="pickAskTerm(term.query)"
                >{{ term.query }}</button>
              </div>
            </div>
            <div v-if="error" class="search__hint search__hint--err">{{ error }}</div>
          </div>
          <button class="ask__back" type="button" @click="backToSearch">
            ← {{ isZh ? '新对话 / 返回搜索' : 'new chat / back' }}
          </button>
        </div>

        <!-- 搜索态 -->
        <div v-else class="search__body">
          <div v-if="loading" class="search__hint">{{ isZh ? '检索中…' : 'searching…' }}</div>
          <div v-else-if="error" class="search__hint search__hint--err">{{ error }}</div>
          <template v-else-if="!q.trim()">
            <div class="search__hint">
              {{
                features.rag
                  ? (isZh ? '输入关键词搜索，或按 Enter 直接问我的博客 ✨' : 'Search, or press Enter to ask my blog ✨')
                  : (isZh ? '输入关键词搜索全站内容' : 'Search across the site')
              }}
            </div>
            <!-- 热门搜索词：仅在搜索框为空时显示 -->
            <div v-if="hotSearchTerms.length > 0" class="search__hot">
              <div class="search__hot-label">{{ isZh ? '热门搜索' : 'Popular' }}</div>
              <div class="search__hot-chips">
                <button
                  v-for="term in hotSearchTerms"
                  :key="term.query"
                  type="button"
                  class="search__hot-chip"
                  @click="pickSearchTerm(term.query)"
                >{{ term.query }}</button>
              </div>
            </div>
          </template>
          <template v-else>
            <!-- 问答入口 (RAG 关掉时不显示) -->
            <button v-if="features.rag" class="ask__cta" type="button" @click="sendTurn">
              <span class="ask__cta-icon">✨</span>
              <span>{{ isZh ? `问我的博客：「${q.trim()}」` : `Ask my blog: "${q.trim()}"` }}</span>
            </button>
            <div v-if="hits.length === 0 && !loading" class="search__hint">
              {{ isZh ? '没有匹配的内容。' : 'no matches.' }}
            </div>
            <div v-else class="search__hits" role="listbox">
              <button
                v-for="h in hits"
                :key="`${h.type}:${h.id}`"
                type="button"
                role="option"
                class="search__hit"
                @click="go(h)"
              >
                <span class="search__hit-type">{{ h.type }}</span>
                <span class="search__hit-title">{{ h.title }}</span>
                <span v-if="h.excerpt" class="search__hit-excerpt">{{ h.excerpt }}</span>
              </button>
            </div>
          </template>
        </div>
      </div>
    </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.search {
  position: fixed;
  inset: 0;
  z-index: 100;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 80px 16px 32px;
  background: rgba(20, 8, 24, 0.72);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
}
.search__panel {
  width: min(680px, 100%);
  max-height: calc(100vh - 120px);
  display: flex;
  flex-direction: column;
  border-radius: 22px;
  background: rgba(40, 20, 40, 0.78);
  border: 1px solid var(--aurora-glass-border-strong);
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
  overflow: hidden;
}
/* 仅进入 embeddings 问答态才亮: 彩色呼吸辉光灯 (紫→青→粉→绿 循环 + brightness 呼吸).
   闲置 / 关键词搜索 / 打字时无灯. 答完保持这个基线速度+亮度. */
.search__panel--glow {
  border-color: transparent;
  animation: ask-glow 3s linear infinite, ask-pulse 1.1s ease-in-out infinite;
}
/* RAG 流式输出中: 加快颜色循环 + 加快加亮呼吸脉冲 */
.search__panel--glow.search__panel--active {
  animation: ask-glow 1.8s linear infinite, ask-pulse-strong 0.6s ease-in-out infinite;
}
@keyframes ask-glow {
  0%   { box-shadow: 0 0 0 1.5px rgba(168, 124, 255, 0.85), 0 0 26px 3px rgba(168, 124, 255, 0.55), 0 24px 80px rgba(0, 0, 0, 0.5); }
  25%  { box-shadow: 0 0 0 1.5px rgba(96, 224, 255, 0.85),  0 0 26px 3px rgba(96, 224, 255, 0.55),  0 24px 80px rgba(0, 0, 0, 0.5); }
  50%  { box-shadow: 0 0 0 1.5px rgba(255, 124, 200, 0.85), 0 0 26px 3px rgba(255, 124, 200, 0.55), 0 24px 80px rgba(0, 0, 0, 0.5); }
  75%  { box-shadow: 0 0 0 1.5px rgba(134, 239, 172, 0.85), 0 0 26px 3px rgba(134, 239, 172, 0.55), 0 24px 80px rgba(0, 0, 0, 0.5); }
  100% { box-shadow: 0 0 0 1.5px rgba(168, 124, 255, 0.85), 0 0 26px 3px rgba(168, 124, 255, 0.55), 0 24px 80px rgba(0, 0, 0, 0.5); }
}
/* 基线呼吸 (结束后保持) */
@keyframes ask-pulse {
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.18); }
}
/* 活跃态: 更亮的呼吸 */
@keyframes ask-pulse-strong {
  0%, 100% { filter: brightness(1.05); }
  50%      { filter: brightness(1.45); }
}
@media (prefers-reduced-motion: reduce) {
  .search__panel--glow,
  .search__panel--glow.search__panel--active { animation: none; box-shadow: 0 0 0 1.5px rgba(168, 124, 255, 0.7), 0 24px 80px rgba(0, 0, 0, 0.5); }
  .ask__cta { animation: none; box-shadow: 0 0 0 1px rgba(168, 124, 255, 0.6); }
}
.search__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  border-bottom: 1px solid rgba(255, 220, 200, 0.1);
}
.search__icon {
  font-size: 18px;
  color: var(--aurora-accent-soft);
}
.search__input {
  flex: 1;
  background: transparent;
  border: none;
  outline: none;
  color: var(--aurora-text);
  font-family: var(--font-display);
  font-size: 18px;
  font-weight: 300;
}
.search__input::placeholder {
  color: var(--aurora-text-faint);
  font-style: italic;
}
.search__close {
  background: transparent;
  border: 1px solid rgba(255, 220, 200, 0.18);
  color: var(--aurora-text-soft);
  border-radius: 100px;
  font-family: var(--font-mono);
  font-size: 11px;
  padding: 4px 10px;
  cursor: pointer;
  letter-spacing: 1px;
}
.search__body {
  flex: 1; /* 占据 panel 内 head 之外的全部高度 */
  min-height: 0; /* 解锁 flex item 默认 min-height: auto，否则 overflow-y 不触发 */
  padding: 12px 8px 16px;
  overflow-y: auto;
  overscroll-behavior: contain; /* 滚到底/顶不传给背景 */
}
.search__hint {
  padding: 24px 16px;
  text-align: center;
  color: var(--aurora-text-mute);
  font-family: var(--font-display);
  font-style: italic;
  font-size: 14px;
}
.search__hint--err {
  color: var(--aurora-magenta);
}
.search__hits {
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin: 0;
  padding: 0;
}
.search__hit {
  display: grid;
  grid-template-columns: 64px 1fr;
  gap: 14px;
  padding: 12px 14px;
  border-radius: 12px;
  cursor: pointer;
  transition: background 0.18s;
  align-items: baseline;
  width: 100%;
  text-align: left;
  background: transparent;
  border: none;
  color: inherit;
  font: inherit;
  outline: none;
}
.search__hit:hover,
.search__hit:focus-visible {
  background: rgba(255, 255, 255, 0.06);
}
.search__hit:active {
  background: rgba(255, 126, 95, 0.12);
}
.search__hit-type {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 1.2px;
  color: var(--aurora-accent-soft);
  text-align: right;
}
.search__hit-title {
  font-family: var(--font-display);
  font-size: 15px;
  color: var(--aurora-text);
  grid-column: 2;
}
.search__hit-excerpt {
  grid-column: 2;
  font-family: var(--font-display);
  font-style: italic;
  font-size: 12px;
  color: var(--aurora-text-mute);
  margin-top: 4px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* ── 问我的博客 (RAG) ── */
.ask__cta {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  margin-bottom: 12px;
  border: 1px solid rgba(134, 239, 172, 0.22);
  background: linear-gradient(100deg, rgba(134, 239, 172, 0.08), rgba(125, 211, 252, 0.06));
  border-radius: 12px;
  color: var(--aurora-text);
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  /* 跟问答态 panel 同款彩色呼吸辉光灯, 引导点击 */
  border-color: transparent;
  animation: ask-glow-sm 3s linear infinite, ask-pulse 1.1s ease-in-out infinite;
}
/* CTA 尺寸的辉光: 颜色循环 + 适中外发光, 不带 panel 的大投影 */
@keyframes ask-glow-sm {
  0%   { box-shadow: 0 0 0 1px rgba(168, 124, 255, 0.75), 0 0 16px 1px rgba(168, 124, 255, 0.45); }
  25%  { box-shadow: 0 0 0 1px rgba(96, 224, 255, 0.75),  0 0 16px 1px rgba(96, 224, 255, 0.45); }
  50%  { box-shadow: 0 0 0 1px rgba(255, 124, 200, 0.75), 0 0 16px 1px rgba(255, 124, 200, 0.45); }
  75%  { box-shadow: 0 0 0 1px rgba(134, 239, 172, 0.75), 0 0 16px 1px rgba(134, 239, 172, 0.45); }
  100% { box-shadow: 0 0 0 1px rgba(168, 124, 255, 0.75), 0 0 16px 1px rgba(168, 124, 255, 0.45); }
}
.ask__cta:hover {
  border-color: rgba(134, 239, 172, 0.45);
  transform: translateY(-1px);
}
.ask__cta-icon {
  font-size: 16px;
}
.ask__answer {
  padding: 4px 4px 8px;
}
/* 多轮对话: 滚动容器 + 气泡 */
.ask__convo {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 4px 4px 8px;
  max-height: 56vh;
  overflow-y: auto;
}
.ask__bubble--user {
  align-self: flex-end;
  max-width: 85%;
  padding: 8px 14px;
  border-radius: 14px 14px 4px 14px;
  background: color-mix(in srgb, var(--aurora-accent, #8b7cff) 22%, transparent);
  color: var(--aurora-text);
  font-size: 14px;
  line-height: 1.6;
  white-space: pre-wrap;
  word-break: break-word;
}
.ask__bubble--ai {
  align-self: flex-start;
  max-width: 100%;
  width: 100%;
}
.ask__text {
  font-size: 15px;
  line-height: 1.75;
  color: var(--aurora-text);
  margin: 0 0 10px;
}
/* RAG 答案的 markdown 渲染产物排版（marked 输出 p/ul/ol/li/strong/em/code/a） */
.ask__text :deep(p) {
  margin: 0 0 12px;
}
.ask__text :deep(p:last-child) {
  margin-bottom: 0;
}
.ask__text :deep(ul),
.ask__text :deep(ol) {
  margin: 0 0 12px;
  padding-left: 1.4em;
}
.ask__text :deep(li) {
  margin: 4px 0;
}
.ask__text :deep(li > p) {
  margin: 0;
}
.ask__text :deep(strong) {
  font-weight: 600;
  color: var(--aurora-text);
}
.ask__text :deep(code) {
  font-family: var(--font-mono, ui-monospace, monospace);
  font-size: 0.9em;
  padding: 1px 5px;
  border-radius: 5px;
  background: color-mix(in srgb, var(--aurora-text) 10%, transparent);
}
.ask__text :deep(pre) {
  margin: 0 0 12px;
  padding: 12px 14px;
  border-radius: 10px;
  overflow-x: auto;
  background: color-mix(in srgb, var(--aurora-text) 8%, transparent);
}
.ask__text :deep(pre code) {
  padding: 0;
  background: none;
}
.ask__text :deep(a) {
  color: var(--aurora-accent, #8b7cff);
  text-decoration: underline;
  text-underline-offset: 2px;
}
.ask__text :deep(h1),
.ask__text :deep(h2),
.ask__text :deep(h3) {
  font-size: 1.05em;
  font-weight: 600;
  margin: 16px 0 8px;
}
.ask__sources {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(255, 245, 232, 0.1);
}
.ask__sources-label {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: var(--aurora-text-mute);
  margin-bottom: 8px;
}
.ask__source {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  border: none;
  background: transparent;
  border-radius: 8px;
  color: var(--aurora-text-soft);
  font-size: 13px;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.ask__source:hover {
  background: rgba(255, 255, 255, 0.05);
}
.ask__source-title {
  color: var(--aurora-text);
}
.ask__back {
  margin-top: 8px;
  background: transparent;
  border: none;
  color: var(--aurora-text-mute);
  font-size: 12px;
  cursor: pointer;
  padding: 6px 4px;
}
.ask__back:hover {
  color: var(--aurora-accent-soft);
}

@media (max-width: 540px) {
  .search {
    padding-top: 24px;
  }
  .search__hit {
    grid-template-columns: 48px 1fr;
    gap: 10px;
  }
}

.overlay-enter-active,
.overlay-leave-active {
  transition: opacity 0.18s ease;
}
.overlay-enter-from,
.overlay-leave-to {
  opacity: 0;
}

/* ── 热门搜索词（搜索态 · 输入框为空时） ── */
.search__hot {
  padding: 4px 8px 12px;
}
.search__hot-label {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--aurora-text-mute);
  margin-bottom: 10px;
}
.search__hot-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.search__hot-chip {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 220, 200, 0.14);
  border-radius: 100px;
  color: var(--aurora-text-soft);
  font-family: var(--font-display);
  font-size: 13px;
  padding: 5px 14px;
  cursor: pointer;
  transition: all 0.18s;
}
.search__hot-chip:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 220, 200, 0.3);
  color: var(--aurora-text);
}

/* ── 推荐问题（ask 态 · 对话为空时） ── */
.ask__suggestions {
  padding: 8px 4px 4px;
}
.ask__suggestions-label {
  font-family: var(--font-mono);
  font-size: 10px;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: var(--aurora-text-mute);
  margin-bottom: 10px;
}
.ask__suggestions-chips {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.ask__suggestion-chip {
  background: rgba(168, 124, 255, 0.07);
  border: 1px solid rgba(168, 124, 255, 0.2);
  border-radius: 10px;
  color: var(--aurora-text-soft);
  font-family: var(--font-display);
  font-size: 13px;
  padding: 8px 14px;
  cursor: pointer;
  text-align: left;
  transition: all 0.18s;
  line-height: 1.4;
}
.ask__suggestion-chip:hover {
  background: rgba(168, 124, 255, 0.14);
  border-color: rgba(168, 124, 255, 0.4);
  color: var(--aurora-text);
  transform: translateX(3px);
}
</style>
